import { pipe } from 'utils/fp';

/**
 * Gets best way to fit the image in the stage.
 * @param {element} image
 * @param {Konva.Layer} stage
 * @param {number} zoom scale multiplier applied after fit, 1 === normal fit
 */
type Dims = { width: number | null; height: number | null };
export const calcScale = (imageDims?: Dims, stageDims?: Dims, zoom = 1): number => {
  if (!imageDims?.height || !stageDims?.height || !imageDims?.width || !stageDims?.width) {
    return 1;
  }

  const layerWidth = stageDims.width;
  const layerHeight = stageDims.height;
  const scaleFactorX = layerWidth / imageDims.width;
  const scaleFactorY = layerHeight / imageDims.height;
  const scaledObjectWillExceedHeight = imageDims.height * scaleFactorX > layerHeight;

  // if scaling to width will exceed height, scale to height instead
  if (scaledObjectWillExceedHeight) {
    return scaleFactorY * zoom;
  }
  return scaleFactorX * zoom;
};

type Dimensions = { width: number; height: number };
type Scaling = { x: number; y: number };
export const aspectRatio = (dims: Dimensions): number => {
  return dims.height / dims.width;
};

export const matchAspectRatio =
  (reference: Dimensions) =>
  (target: Dimensions): Dimensions => {
    const ar = aspectRatio(reference);
    const height = ar * target.width;
    const width = target.height / ar;
    return { width, height };
  };

export const calculateScaleFactor =
  (reference: Dimensions) =>
  (target: Dimensions): Scaling => {
    const x = reference.height / target.height;
    const y = reference.width / target.width;
    return { x, y };
  };

export const scaleToFit =
  (reference: Dimensions) =>
  (target: Dimensions): Scaling => {
    // in here we pipe the target dims to first match the ar of the reference so that measures are in the right ar
    // then with the same ar, we can easily calculate the scale factor to match the target dims
    return pipe(target, pipe(reference, matchAspectRatio), pipe(reference, calculateScaleFactor));
  };
